import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { User } from "../types/auth";
import { authService } from "../services/authService";
import * as Sentry from "@sentry/react";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  showPreviewFeatures: boolean;
  togglePreviewFeatures: (show: boolean) => void;
  login: (email: string, password: string) => Promise<void>;
  continueAsGuest: () => Promise<void>;
  signup: (email: string, password: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPreviewFeatures, setShowPreviewFeatures] = useState<boolean>(
    () => {
      // Initialize from localStorage or default to false
      const savedPreference = localStorage.getItem("showPreviewFeatures");
      if (savedPreference !== null) {
        return savedPreference === "true";
      }
      // Default to false if not set
      return false;
    }
  );
  const trackedAppOpenUserId = useRef<string | null>(null);

  // Set Sentry user context when user changes
  useEffect(() => {
    if (user) {
      // Identify user in Sentry
      Sentry.setUser({
        id: user.id?.toString(),
        email: user.email,
        username: user.name,
      });
    } else {
      // Clear user data when logged out
      Sentry.setUser(null);
    }
  }, [user]);

  useEffect(() => {
    const loadUser = async () => {
      try {
        // First check if we have a stored user
        const storedUser = authService.getCurrentUser();
        if (storedUser) {
          setUser(storedUser);
          setLoading(false);
          return;
        }

        // If no stored user, try to fetch from API
        const currentUser = await authService.fetchCurrentUser();
        setUser(currentUser);
      } catch (error) {
        console.error("Error loading user:", error);
        Sentry.captureException(error);
      } finally {
        setLoading(false);
      }
    };
    loadUser();
  }, []);

  useEffect(() => {
    if (!user?.id) {
      trackedAppOpenUserId.current = null;
      return;
    }

    if (trackedAppOpenUserId.current === user.id) {
      return;
    }

    trackedAppOpenUserId.current = user.id;

    const trackAppOpen = async () => {
      try {
        const updatedUser = await authService.trackAppOpen();
        if (updatedUser) {
          setUser(updatedUser);
        }
      } catch (error) {
        console.error("Error tracking app open:", error);
        Sentry.captureException(error);
      }
    };

    trackAppOpen();
  }, [user?.id]);

  const login = async (email: string, password: string) => {
    try {
      const user = await authService.login({ email, password });
      setUser(user);
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  const signup = async (email: string, password: string, name: string) => {
    try {
      const user = await authService.signup({ email, password, name });
      setUser(user);
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  const continueAsGuest = async () => {
    try {
      const user = await authService.continueAsGuest();
      setUser(user);
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Stop any active meditation playback by dispatching a custom event
      // This is a better pattern than trying to use context directly in this function
      const logoutEvent = new CustomEvent("user-logout");
      window.dispatchEvent(logoutEvent);

      await authService.logout();
      setUser(null);
      // Clear user data from Sentry when logged out
      Sentry.setUser(null);
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  const resetPassword = async (newPassword: string) => {
    try {
      const user = await authService.resetPassword(newPassword);
      setUser(user);
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  const togglePreviewFeatures = (show: boolean) => {
    setShowPreviewFeatures(show);
    localStorage.setItem("showPreviewFeatures", show.toString());
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        showPreviewFeatures,
        togglePreviewFeatures,
        login,
        continueAsGuest,
        signup,
        logout,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
